import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import Table from "../../../core/common/dataTable/index"
import { getAllSystems, updateSystem, type BoardSystem } from "../../../service/systems"

// Deliberately view + edit name/description/status only -- code and style
// drive real grading logic app-wide and are read-only here, never
// submitted. No create, no delete: a system's code/style identity isn't
// something this page manages. See RMS_PORT_TRACKER.md "Board Systems".
const BoardSystems = () => {
  const [items, setItems] = useState<BoardSystem[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<BoardSystem | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const fetchItems = async () => {
    setLoading(true)
    try {
      const res = await getAllSystems()
      setItems(res?.data || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load board systems")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchItems()
  }, [])

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Code", dataIndex: "code" },
    {
      title: "Style",
      dataIndex: "style",
      render: (v: string) => (v === "cambridge" ? "Cambridge" : "Local"),
    },
    { title: "Description", dataIndex: "description" },
    {
      title: "Status",
      dataIndex: "status",
      render: (v: string) =>
        v === "inactive" ? (
          <span className="badge bg-secondary-transparent text-secondary">Inactive</span>
        ) : (
          <span className="badge bg-success-transparent text-success">Active</span>
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, r: BoardSystem) => (
        <Link to="#" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(r)}>
          <i className="ti ti-edit" />
        </Link>
      ),
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Board Systems</h3>
            <p className="text-muted mb-0">
              Name, description and status only — code and style drive grading rules
              elsewhere and can't be changed here.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              dataSource={items}
              columns={columns}
              loading={loading}
              pagination={{
                currentPage: page,
                limit: pageSize,
                total: items.length,
                onChange: (p: number, ps: number) => {
                  setPage(p)
                  setPageSize(ps)
                },
              }}
            />
          </div>
        </div>
      </div>

      {editing && (
        <BoardSystemModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            fetchItems()
          }}
        />
      )}
    </div>
  )
}

const BoardSystemModal = ({
  editing,
  onClose,
  onSaved,
}: {
  editing: BoardSystem
  onClose: () => void
  onSaved: () => void
}) => {
  const [name, setName] = useState(editing.name)
  const [description, setDescription] = useState(editing.description)
  const [status, setStatus] = useState<"active" | "inactive">(editing.status)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const goingInactive = status === "inactive" && editing.status !== "inactive"

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError("Name is required")
      return
    }
    if (!description.trim()) {
      setError("Description is required")
      return
    }
    setSaving(true)
    setError(null)
    try {
      await updateSystem(editing._id, { name: name.trim(), description: description.trim(), status })
      toast.success("Updated")
      onSaved()
    } catch (e2: any) {
      setError(e2?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit board system</h4>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Code</label>
                    <input className="form-control" value={editing.code} disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Style</label>
                    <input
                      className="form-control"
                      value={editing.style === "cambridge" ? "Cambridge" : "Local"}
                      disabled
                    />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Name</label>
                    <input
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as "active" | "inactive")}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {goingInactive && (
                  <div className="alert alert-warning py-2 px-3 small mb-0">
                    Setting this inactive hides it from every dropdown app-wide
                    (admissions, enrollment, class setup, etc.) — only do this if
                    the board is genuinely being retired.
                  </div>
                )}

                {error && <div className="alert alert-danger mt-3 mb-0 py-2 px-3 small">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving…" : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default BoardSystems
